/**
 * SkillDelivery Platform
 *
 * Copyright © 2026 SKD RATWANI Technology & Software Solutions Pvt Ltd
 * All Rights Reserved.
 *
 * PROPRIETARY AND CONFIDENTIAL
 *
 * This source code is the exclusive property of SKD RATWANI Technology &
 * Software Solutions Pvt Ltd, operating as "SkillDelivery". Unauthorized
 * copying, modification, distribution, or use of this software, via any
 * medium, is strictly prohibited without express written permission.
 *
 * For licensing inquiries: legal@skilldelivery.co.in
 *
 * Registered Office: Sitapur, Uttar Pradesh, India - 261001
 * Jurisdiction: Noida, Uttar Pradesh, India - 201301
 */

import Settings from './pages/Settings';
import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AdminAuthProvider } from './contexts/AdminAuthContext';
import AuthWrapper from './components/AuthWrapper';
import ChatShare from './components/ChatShare';
import SkillBotEnhanced from './components/SkillBotEnhanced';
import ProtectedRoute from './components/ProtectedRoute';

// Auth Pages
import Login from './pages/Login';
import Register from './pages/Register';
import ChangePassword from './pages/ChangePassword';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import VerifyEmail from './pages/VerifyEmail';
import Terms from './pages/Terms';
import Suspended from './pages/Suspended';

// Public Marketing & Onboarding (logged-out visitors)
import WelcomeRouter from './pages/WelcomeRouter';
import OrganizationLanding from './pages/OrganizationLanding';
import TourPage from './pages/TourPage';
import DomainLanding from './pages/DomainLanding';
import ManifestoPage from './pages/ManifestoPage';
import IndividualLanding from './pages/IndividualLanding';
import UniversityLanding from './pages/UniversityLanding';
import InstituteLanding from './pages/InstituteLanding';

// WMS Showcase (Layer 1: Case Studies, Layer 3: Resources, Layer 4: Simulation)
import CaseStudiesIndex from './pages/showcase/CaseStudiesIndex';
import CaseStudyDetail from './pages/showcase/CaseStudyDetail';
import ResourcesLibrary from './pages/showcase/ResourcesLibrary';
import WmsSimulationShowcase from './pages/showcase/WmsSimulationShowcase';

// Skill.Civil Showcase — mirrors WMS shape (public, unauth, reads V2 Supabase
// case_studies WHERE domain='civil'). Kept in separate files rather than
// parameterizing the WMS pages because the family-legacy narrative renders
// firms/generations/sectors/projects/verbatim-values sections that the WMS
// challenge/solution/outcomes-table shape can't express.
import CivilCaseStudiesIndex from './pages/showcase/CivilCaseStudiesIndex';
import CivilCaseStudyDetail from './pages/showcase/CivilCaseStudyDetail';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import AdminDashboard from './pages/admin/AdminDashboard';
import SecurityEvents from './pages/admin/SecurityEvents';
import TrainingJobs from './pages/admin/TrainingJobs';
import TrainingJobDetail from './pages/admin/TrainingJobDetail';
import SubmitJob from './pages/admin/SubmitJob';
import AdapterManagement from './pages/admin/AdapterManagement';
import AdapterDetails from './pages/admin/AdapterDetails';
import AdapterBenchmark from './pages/admin/AdapterBenchmark';
// Training Data Management
import TrainingDataManagement from './pages/admin/TrainingDataManagement';

// Cloud Services Dashboard
import CloudServicesDashboard from './pages/admin/CloudServicesDashboard';

//2FA
import AdminSecurity from './pages/admin/AdminSecurity';

//evaluators
import EvaluationDashboard from './pages/admin/EvaluationDashboard';

// System Settings
import SystemSettings from './pages/admin/SystemSettings';

// User Management
import UserManagement from './pages/admin/UserManagement';

// Smart Invite (Phase 1)
import ConnectCatalog from './pages/admin/ConnectCatalog';
// R14 Early Access spam containment -- admin leads filter + Not-spam.
import ConnectLeads from './pages/admin/ConnectLeads';
import InviteWelcome from './pages/InviteWelcome';
import PendingInviteClaimer from './components/invite/PendingInviteClaimer';
// R13 Web Connect -- Round 1. Owner-area under /connect/*. The layout
// gates on features.connect; server-side `requireConnectAccess` stays
// authoritative on every API call.
import ConnectLayout from './pages/connect/ConnectLayout';
import ConnectContactsList from './pages/connect/ContactsListPage';
import ConnectContactDetail from './pages/connect/ContactDetailPage';
import ConnectInviteReview from './pages/connect/InviteReviewPage';
import ConnectFeedbackInbox from './pages/connect/FeedbackInboxPage';
import ConnectFeedbackDetail from './pages/connect/FeedbackDetailPage';
import ConnectSettings from './pages/connect/SettingsPage';
// Release 12 Step 2 — public /q/<handle> contact-card page.
import ContactCard from './pages/ContactCard';

// Phase 2B: Adapter Hierarchy
import AdapterHierarchy from './pages/admin/AdapterHierarchy';

// Adapter Upload (Modal via HuggingFace)
import AdapterUpload from './pages/admin/AdapterUpload';

// Phase 2E: Prompt Management
import PromptManagement from './pages/admin/PromptManagement';
// R13 Step 4 P1 -- ConnectEditor merged into ConnectCatalog; this
// import + the /admin/connect-editor route below are deleted.

// Civil Construction: Risk Assessment
import RiskAssessment from './pages/admin/RiskAssessment';

// API Credits Dashboard
import Credits from './pages/admin/Credits';

// SEAL R&D Hub (Phase 2, Step 2.4)
import SealDashboard from './pages/admin/SealDashboard';
import SealConfig from './pages/admin/SealConfig';
import SealCandidates from './pages/admin/SealCandidates';

// Wiki Intelligence Dashboard
import WikiDashboard from './pages/admin/WikiDashboard';

// Partnership GTM Dashboard
import PartnershipDashboard from './pages/admin/PartnershipDashboard';

// Research Studio
import ResearchStudio from './pages/admin/ResearchStudio';

// SkillForge Review — approve teacher answers before they become training data
import SkillForgeReview from './pages/admin/SkillForgeReview';

// Persona Agents (Phase 1 scaffold — backend returns 501 until V2 lands)
import PersonaAgents from './pages/admin/PersonaAgents';

// Workflow Factory — Catalog / Detail / Run Viewer (WF-4)
import WorkflowsCatalog from './pages/admin/WorkflowsCatalog';
import WorkflowDetail from './pages/admin/WorkflowDetail';
import RunViewer from './pages/admin/RunViewer';
import AgentRunner from './pages/admin/AgentRunner';

// Spine — per-vertical seed-question editor (retrain spine)
import SpineSeeds from './pages/admin/SpineSeeds';
import CurriculumGaps from './pages/admin/CurriculumGaps';

// Putaway Intelligence — PUBLIC showcase mount. Same component is also
// React.lazy'd inside AuthWrapper for the gated /wms/putaway-intelligence
// route; lazy here keeps the ~700KB plotly.js bundle out of main.js.
const PutawayIntelligencePage = React.lazy(() => import('./pages/PutawayIntelligencePage'));

/**
 * Blocks admin routes on app domain
 * Admin only accessible from secret admin domain
 */
const AdminDomainGuard = ({ children }) => {
  const BLOCKED_DOMAINS = [
    'app.skilldelivery.co.in',
    'skilldelivery-platform.vercel.app'
  ];
  const currentDomain = window.location.hostname;
  if (BLOCKED_DOMAINS.includes(currentDomain)) {
    return <Navigate to="/" replace />;
  }
  return children;
};

/**
 * Conditional SkillBot Renderer
 * Shows SkillBot only when:
 * - User is logged in (authenticated)
 * - NOT on admin routes
 * - NOT on login/register pages
 * - NOT on shared chat pages
 * - NOT on password management pages
 * - NOT on /skillbot page (full-page SkillBot already shown)
 */
const ConditionalSkillBot = () => {
  const location = useLocation();
  const auth = useAuth();

  // Routes where SkillBot should NOT appear
  const isAdminRoute = location.pathname.startsWith('/admin');
  const isSharePage = location.pathname.startsWith('/share');
  const isSkillBotPage = location.pathname === '/skillbot'; // Hide on dedicated SkillBot page
  const isAuthPage = ['/login', '/register', '/change-password', '/forgot-password', '/reset-password', '/verify-email', '/terms', '/suspended'].includes(location.pathname);

  // Check if user is authenticated
  // Handle both possible auth context structures
  const isAuthenticated = auth?.isAuthenticated || auth?.user || !!localStorage.getItem('token');

  // Debug log (remove in production)
  console.log('[SkillBot] Path:', location.pathname, 'Auth:', isAuthenticated, 'Admin:', isAdminRoute);

  // Only show SkillBot if authenticated AND not on restricted pages
  if (!isAuthenticated || isAdminRoute || isSharePage || isAuthPage || isSkillBotPage) {
    return null;
  }

  return <SkillBotEnhanced />;
};

/**
 * Guest Route - Redirect to dashboard if already logged in
 */
const GuestRoute = ({ children }) => {
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || auth?.user || !!localStorage.getItem('token');

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

/**
 * First-visit redirect: send brand-new unauthenticated visitors
 * landing on "/" to the /welcome marketing page. Returns null
 * (no-op) on every other path, for repeat visitors, and for
 * logged-in users (who get their dashboard via AuthWrapper).
 */
const FirstVisitRedirect = () => {
  const auth = useAuth();
  const isAuthenticated = auth?.isAuthenticated || auth?.user || !!localStorage.getItem('token');

  if (isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  if (localStorage.getItem('skd_welcome_seen') === 'true') {
    return <Navigate to="/login" replace />;
  }

  return <Navigate to="/welcome" replace />;
};

function AppContent() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        {/* SkillBot floating widget - appears on user-facing pages only */}
        <ConditionalSkillBot />

        {/* Smart Invite — picks up a pendingInvite from localStorage after
            a freshly-registered user lands anywhere in the app, calls
            /api/public/invites/:token/claim with their JWT, then clears
            the entry. Idempotent + silent; renders nothing. */}
        <PendingInviteClaimer />

        <Routes>
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PUBLIC AUTH ROUTES                                              */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route 
            path="/login" 
            element={
              <GuestRoute>
                <Login />
              </GuestRoute>
            } 
          />
          <Route 
            path="/register" 
            element={
              <GuestRoute>
                <Register />
              </GuestRoute>
            } 
          />
          <Route path="/change-password" element={<ChangePassword />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/suspended" element={<Suspended />} />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* ADMIN ROUTES                                                    */}
          {/* ═══════════════════════════════════════════════════════════════ */}

          <Route path="/admin" element={<AdminDomainGuard><Navigate to="/admin/login" replace /></AdminDomainGuard>} />
 
          
          

          <Route path="/admin/login" element={<AdminDomainGuard><AdminLogin /></AdminDomainGuard>} />

          <Route
            path="/admin/dashboard"
            element={
              <ProtectedRoute>
                <AdminDashboard />
              </ProtectedRoute>
            }
          />

          {/* Security phase — audit browser (admin.audit.view) */}
          <Route
            path="/admin/security-events"
            element={
              <ProtectedRoute>
                <SecurityEvents />
              </ProtectedRoute>
            }
          />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* CLOUD SERVICES DASHBOARD                                        */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route
            path="/admin/cloud-services"
            element={
              <ProtectedRoute>
                <CloudServicesDashboard />
              </ProtectedRoute>
            }
          />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* SYSTEM SETTINGS                                                  */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route
            path="/admin/settings"
            element={
              <ProtectedRoute>
                <SystemSettings />
              </ProtectedRoute>
            }
          />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* TRAINING DATA MANAGEMENT                                        */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route
            path="/admin/training/data"
            element={
              <ProtectedRoute anyPermissions={['training.view', 'training.submit']}>
                <TrainingDataManagement />
              </ProtectedRoute>
            }
          />

          <Route 
            path="/settings" 
            element={
               <ProtectedRoute>
                 <Settings />
               </ProtectedRoute>
             } 
          />

          <Route
            path="/admin/training/jobs/:jobId"
            element={
              <ProtectedRoute anyPermissions={['training.view', 'training.submit']}>
                <TrainingJobDetail />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/training/jobs"
            element={
              <ProtectedRoute anyPermissions={['training.view', 'training.submit']}>
                <TrainingJobs />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/training/submit"
            element={
              <ProtectedRoute permission="training.submit">
                <SubmitJob />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/training/adapters/:adapterId"
            element={
              <ProtectedRoute anyPermissions={['adapter.view', 'adapter.activate']}>
                <AdapterDetails />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/training/adapters"
            element={
              <ProtectedRoute anyPermissions={['adapter.view', 'adapter.activate']}>
                <AdapterManagement />
              </ProtectedRoute>
            }
          />

          {/* Adapter Upload to Modal via HuggingFace */}
          <Route
            path="/admin/adapter-upload"
            element={
              <ProtectedRoute anyPermissions={['adapter.view', 'adapter.activate']}>
                <AdapterUpload />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/benchmark"
            element={
              <ProtectedRoute permission="adapter.test">
                <AdapterBenchmark />
              </ProtectedRoute>
            }
          />

          <Route path="/admin/security" element={<AdminSecurity />} />

          <Route
            path="/admin/evaluations"
            element={
              <ProtectedRoute>
                <EvaluationDashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/admin/users"
            element={
              <ProtectedRoute>
                <UserManagement />
              </ProtectedRoute>
            }
          />

          {/* Smart Invite catalog (super admin). Kill-switch gated server-side. */}
          <Route
            path="/admin/connect-catalog"
            element={
              <ProtectedRoute>
                <ConnectCatalog />
              </ProtectedRoute>
            }
          />

          {/* R14 Early Access leads (super admin). Suspected spam
              filter + one-click Not-spam clear. */}
          <Route
            path="/admin/connect-leads"
            element={
              <ProtectedRoute>
                <ConnectLeads />
              </ProtectedRoute>
            }
          />

          {/* Smart Invite public welcome page — NO auth. Token-gated server-side. */}
          <Route path="/invite/:token" element={<InviteWelcome />} />
          {/* R12 Step 2 — public /q/<handle> contact-card. No auth. */}
          <Route path="/q/:handle" element={<ContactCard />} />

          {/* R13 Web Connect Round 1 -- owner-area Connect UI.
              NOT wrapped in <ProtectedRoute>: that component only
              recognises the ADMIN auth state (useAdminAuth) and
              bounces non-admin users to /admin/login. Connect pages
              are for ordinary end users; ConnectLayout handles its
              own auth gate (localStorage.token -> /login?returnTo=;
              features.connect from /users/me -> "Request Connect"
              card). Server-side `requireConnectAccess` is
              authoritative on every API call. */}
          <Route
            path="/connect"
            element={<ConnectLayout />}
          >
            <Route index element={<Navigate to="/connect/contacts" replace />} />
            <Route path="contacts" element={<ConnectContactsList />} />
            <Route path="contacts/:id" element={<ConnectContactDetail />} />
            <Route path="contacts/:id/invite" element={<ConnectInviteReview />} />
            <Route path="feedback" element={<ConnectFeedbackInbox />} />
            <Route path="feedback/:id" element={<ConnectFeedbackDetail />} />
            <Route path="settings" element={<ConnectSettings />} />
          </Route>

          {/* Phase 2B: Adapter Hierarchy */}
          <Route
            path="/admin/adapter-hierarchy"
            element={
              <ProtectedRoute>
                <AdapterHierarchy />
              </ProtectedRoute>
            }
          />

          {/* Phase 2E: Prompt Management */}
          <Route
            path="/admin/prompts"
            element={
              <ProtectedRoute>
                <PromptManagement />
              </ProtectedRoute>
            }
          />

          {/* R13 Step 4 P1 -- /admin/connect-editor removed; its tabs
              merged into the live /admin/connect-catalog page. */}

          {/* Civil Construction: Risk Assessment */}
          <Route
            path="/admin/risk-assessment"
            element={
              <ProtectedRoute>
                <RiskAssessment />
              </ProtectedRoute>
            }
          />

          {/* API Credits Dashboard */}
          <Route
            path="/admin/credits"
            element={
              <ProtectedRoute>
                <Credits />
              </ProtectedRoute>
            }
          />

          {/* SEAL R&D Hub (Phase 2, Step 2.4) */}
          <Route
            path="/admin/seal/dashboard"
            element={
              <ProtectedRoute>
                <SealDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seal/config"
            element={
              <ProtectedRoute>
                <SealConfig />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/seal/candidates"
            element={
              <ProtectedRoute>
                <SealCandidates />
              </ProtectedRoute>
            }
          />

          {/* Wiki Intelligence Dashboard */}
          <Route
            path="/admin/wiki"
            element={
              <ProtectedRoute>
                <WikiDashboard />
              </ProtectedRoute>
            }
          />

          {/* Partnership GTM Dashboard */}
          <Route
            path="/admin/partnerships"
            element={
              <ProtectedRoute>
                <PartnershipDashboard />
              </ProtectedRoute>
            }
          />

          {/* Research Studio */}
          <Route
            path="/admin/research"
            element={
              <ProtectedRoute>
                <ResearchStudio />
              </ProtectedRoute>
            }
          />

          {/* SkillForge Review — teacher-answer reviewer (V2 proxy) */}
          <Route
            path="/admin/skillforge/review"
            element={
              <ProtectedRoute>
                <SkillForgeReview />
              </ProtectedRoute>
            }
          />

          {/* Persona Agents (Phase 1 scaffold) */}
          <Route
            path="/admin/persona-agents"
            element={
              <ProtectedRoute>
                <PersonaAgents />
              </ProtectedRoute>
            }
          />

          {/* Workflow Factory — catalog + detail (WF-4 Slice 2) */}
          <Route
            path="/admin/workflows"
            element={
              <ProtectedRoute>
                <WorkflowsCatalog />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/workflows/:id"
            element={
              <ProtectedRoute>
                <WorkflowDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/workflow-runs/:id"
            element={
              <ProtectedRoute>
                <RunViewer />
              </ProtectedRoute>
            }
          />

          {/* Run-Agent viewer — calls SkillAgent HTTP service via V1 proxy */}
          <Route
            path="/admin/agents/run"
            element={
              <ProtectedRoute>
                <AgentRunner />
              </ProtectedRoute>
            }
          />

          {/* Spine — per-vertical seed-question editor */}
          <Route
            path="/admin/spine/seeds"
            element={
              <ProtectedRoute permission="spine.seeds.manage">
                <SpineSeeds />
              </ProtectedRoute>
            }
          />

          {/* S0 Curriculum Gap Engine — pre-engagement analysis */}
          <Route
            path="/admin/curriculum-gaps"
            element={
              <ProtectedRoute permission="curriculum_gaps.manage">
                <CurriculumGaps />
              </ProtectedRoute>
            }
          />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PUBLIC ROUTES                                                   */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* Public shared chat view */}
          <Route path="/share/:shareId" element={<ChatShare />} />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PUBLIC MARKETING / ONBOARDING ROUTES                            */}
          {/* ═══════════════════════════════════════════════════════════════ */}

          {/* Root: triage by auth + first-visit flag (wins over /* catch-all) */}
          <Route path="/" element={<FirstVisitRedirect />} />

          <Route
            path="/welcome"
            element={
              <GuestRoute>
                <WelcomeRouter />
              </GuestRoute>
            }
          />
          {/* Entity landings (canonical) */}
          <Route path="/entity/individual" element={<IndividualLanding />} />
          <Route path="/entity/organization" element={<OrganizationLanding />} />
          <Route path="/entity/university" element={<UniversityLanding />} />
          <Route path="/entity/institute" element={<InstituteLanding />} />

          {/* Legacy redirects → canonical entity routes */}
          <Route path="/professional" element={<Navigate to="/entity/individual" replace />} />
          <Route path="/organization" element={<Navigate to="/entity/organization" replace />} />
          <Route path="/educator" element={<Navigate to="/entity/university" replace />} />

          <Route path="/tour" element={<TourPage />} />
          <Route path="/manifesto" element={<ManifestoPage />} />
          <Route path="/why" element={<ManifestoPage />} />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* WMS SHOWCASE (under /domain/wms IA)                             */}
          {/* Layer 1: Case Studies · Layer 3: Resources · Layer 4: Simulation */}
          {/* Declared before /domain/:name to be explicit about precedence.  */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route path="/domain/wms/case-studies" element={<CaseStudiesIndex />} />
          <Route path="/domain/wms/case-studies/:slug" element={<CaseStudyDetail />} />
          <Route path="/domain/wms/resources" element={<ResourcesLibrary />} />
          <Route path="/domain/wms/showcase" element={<WmsSimulationShowcase />} />

          {/* Skill.Civil showcase — public, unauth. Mirrors the WMS route shape 1:1.
              Declared before /domain/:name (below) to be explicit about precedence. */}
          <Route path="/domain/civil/case-studies" element={<CivilCaseStudiesIndex />} />
          <Route path="/domain/civil/case-studies/:slug" element={<CivilCaseStudyDetail />} />

          {/* Putaway Intelligence — PUBLIC showcase. Same component also
              mounts inside AuthWrapper at /wms/putaway-intelligence (gated). */}
          <Route
            path="/putaway-intelligence"
            element={
              <React.Suspense fallback={<div className="p-10 text-gray-500">Loading Putaway Intelligence…</div>}>
                <PutawayIntelligencePage />
              </React.Suspense>
            }
          />

          <Route path="/domain/:name" element={<DomainLanding />} />

          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* PROTECTED USER ROUTES (Dashboard, Profile, Roadmap, etc.)       */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          <Route path="/*" element={<AuthWrapper />} />
        </Routes>

      </AdminAuthProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
